import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from 'next-themes';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { clockApi, vacationApi, remoteApi, userApi, calendarApi, reportApi, directoryApi } from '../utils/api';
import { ValorizaLogo } from '../components/ValorizaLogo';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TourGuide } from '../components/TourGuide';
import { MyTimeReminderDialog } from '../components/MyTimeReminderDialog';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  Home, Clock, ClipboardList, CalendarDays, LogOut, Sun, Moon, Globe,
  Palmtree, Laptop, CheckCircle2, XCircle, Loader, ChevronRight,
  LogIn, LogOut as LogOutIcon, Timer, Plus, X, ArrowLeftRight, Info,
  Shield, CheckCircle, ChevronDown, ChevronUp, AlertCircle, AlertTriangle,
  ExternalLink, RefreshCw, Sparkles, Download, Coffee, Mail
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────
const MAX_PLAN_DAYS = 6;

function fmtTime(iso) {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(str) {
  if (!str) return '';
  return new Date(str + 'T12:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long' });
}
function hoursLabel(h) {
  if (h == null) return '--';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}
function isWeekendDay(d) {
  const day = new Date(d + 'T12:00').getDay();
  return day === 0 || day === 6;
}

const STATUS_LABELS = {
  pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado',
  cancelled: 'Cancelado', cancellation_requested: 'Cancelación pendiente',
};
const STATUS_CLS = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400',
  cancellation_requested: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};
const periodLabels = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };

// ─── Tiny components ─────────────────────────────────────────────────────────
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <span className="font-mono font-bold tracking-wider tabular-nums">{now.toLocaleTimeString('es-ES')}</span>;
}

function StatusBadge({ status }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[status] || 'bg-muted text-muted-foreground'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function BalancePill({ icon: Icon, label, value, color }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-white/90 text-xs font-medium backdrop-blur-sm bg-white/20`}>
      <Icon className="h-3.5 w-3.5" />
      <span>{label}: <strong className="text-white">{value}d</strong></span>
    </div>
  );
}

// ─── WEEKLY MINI CALENDAR ────────────────────────────────────────────────────
function WeeklyMiniCalendar({ remotes = [] }) {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon ...
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const remoteByDate = {};
  remotes.forEach(r => {
    if (r.date && (r.status === 'approved' || r.status === 'pending')) {
      remoteByDate[r.date] = r.status;
    }
  });

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  return (
    <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Esta semana</h2>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500 inline-block" />Aprobado</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Pendiente</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {weekDays.map((day, i) => {
          const dateStr = day.toISOString().split('T')[0];
          const isToday = dateStr === today.toISOString().split('T')[0];
          const isWeekend = i >= 5;
          const status = remoteByDate[dateStr];
          const dayNum = day.getDate();

          let dayClass = 'flex flex-col items-center justify-center rounded-xl py-2 text-center transition-all';
          let numClass = 'text-sm font-semibold';

          if (isToday) {
            dayClass += ' ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/20';
            numClass += ' text-emerald-600 dark:text-emerald-400';
          } else if (isWeekend) {
            dayClass += ' opacity-40';
            numClass += ' text-slate-400';
          } else {
            dayClass += ' hover:bg-slate-50 dark:hover:bg-slate-700/50';
            numClass += ' text-slate-700 dark:text-slate-200';
          }

          return (
            <div key={dateStr} className={dayClass} data-testid={`week-day-${dateStr}`}>
              <span className="text-[9px] text-slate-400 dark:text-slate-500 mb-1">{dayNames[i]}</span>
              <span className={numClass}>{dayNum}</span>
              {status && (
                <span className={`w-2 h-2 rounded-full mt-1 ${status === 'approved' ? 'bg-sky-500' : 'bg-amber-400'}`} />
              )}
              {!status && !isWeekend && (
                <span className="w-2 h-2 mt-1 opacity-0">_</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CONTACT HR DIALOG ───────────────────────────────────────────────────────
function ContactHRDialog({ open, onClose }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await reportApi.contactHR(message.trim());
      toast.success('Consulta enviada a RRHH. Te responderán a tu email.');
      setMessage('');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar la consulta. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="contact-hr-dialog">
        <DialogHeader>
          <DialogTitle>Enviar consulta a RRHH</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Si algo no te cuadra con tu saldo o tus solicitudes, escríbelo aquí. Se enviará junto con tu saldo actual directamente a RRHH.
        </p>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ej: Me faltan 2 días de vacaciones de marzo que ya disfruté..."
          rows={4}
          data-testid="contact-hr-message"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contact-hr-cancel">Cancelar</Button>
          <Button onClick={handleSend} disabled={sending || !message.trim()} data-testid="contact-hr-send">
            {sending ? <Loader className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Enviar consulta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── HOME SECTION ────────────────────────────────────────────────────────────
function HomeSection({ user, balance, monthlyRemote, loading, vacations, remotes, navigate, canTelework, setActiveTab, showTrebolDialog, setShowTrebolDialog }) {
  const [showContactHR, setShowContactHR] = useState(false);
  const vacAvail = balance ? (balance.vacation_total + (balance.carryover_days || 0)) - balance.vacation_used - balance.vacation_pending : 0;
  // Use full_days_available as primary metric (0-6). Always clear and predictable.
  const remFullAvail = monthlyRemote
    ? (monthlyRemote.full_days_available ?? monthlyRemote.available ?? 0)
    : (balance ? balance.remote_total - balance.remote_used - balance.remote_pending : 0);
  const remHalfAvail = monthlyRemote?.half_days_available ?? null;
  const remHalfUsed  = monthlyRemote?.half_days_used ?? 0;
  const remFullUsed  = monthlyRemote?.full_days_used ?? 0;
  const remAvail = remFullAvail; // alias for balance card

  const allRequests = [
    ...(vacations || []).map(r => ({ ...r, kind: 'vacation' })),
    ...(remotes || []).map(r => ({ ...r, kind: 'remote' })),
  ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 6);

  const vacationActionDisabled = !user?.fecha_inicio;
  const vacationDisabledMsg = 'Tu puesto y calendario no están formalizados (falta fecha de inicio). Contacta con Admón. de Personal.';

  const quickActions = [
    ...(user?.vacations_enabled ? [{
      icon: Palmtree, label: 'Solicitar Vacaciones',
      color: vacationActionDisabled
        ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed shadow-none'
        : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200 dark:shadow-emerald-900/30',
      action: () => {
        if (vacationActionDisabled) { alert(vacationDisabledMsg); return; }
        navigate('/vacations/new');
      },
      disabled: vacationActionDisabled,
      tooltip: vacationActionDisabled ? vacationDisabledMsg : undefined,
      testid: 'portal-request-vacation',
    }] : []),
    {
      icon: Laptop, label: 'Informar Teletrabajo',
      color: 'bg-sky-500 hover:bg-sky-600 text-white shadow-sky-200 dark:shadow-sky-900/30',
      action: () => canTelework ? navigate('/remote/new') : setShowTrebolDialog(true),
      testid: 'portal-request-remote',
    },
    ...(user?.convenio_id ? [{
      icon: Coffee, label: 'Asunto Propio',
      color: 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-200 dark:shadow-amber-900/30',
      action: () => navigate('/personal-days/new'), testid: 'portal-request-personal',
    }] : []),
    {
      icon: Clock, label: 'Fichar Entrada/Salida',
      color: 'bg-violet-500 hover:bg-violet-600 text-white shadow-violet-200 dark:shadow-violet-900/30',
      action: () => setActiveTab('clock'), testid: 'portal-goto-clock',
    },
    {
      icon: CalendarDays, label: 'Plan Anual',
      color: 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-200 dark:shadow-orange-900/30',
      action: () => setActiveTab('plan'), testid: 'portal-goto-plan',
    },
    // Informe IA deshabilitado temporalmente (hilo "Flex - Teletrabajo duda") — descomentar para reactivar:
    // {
    //   icon: Sparkles, label: 'Informe IA',
    //   color: 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700',
    //   action: () => navigate('/ai-report'), testid: 'portal-ai-report',
    // },
    {
      icon: ClipboardList, label: 'Todas mis solicitudes',
      color: 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700',
      action: () => setActiveTab('requests'), testid: 'portal-all-requests',
    },
  ];

  return (
    <motion.div key="home" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
      {/* Balance cards */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Vacaciones disponibles', value: vacAvail, total: balance?.vacation_total, used: balance?.vacation_used, pending: balance?.vacation_pending, color: 'emerald', icon: Palmtree, testid: 'portal-vacation-balance', carryover: balance?.carryover_days || 0 },
          { label: 'Teletrabajo este mes', value: remFullAvail, total: 6, used: remFullUsed, pending: 0, color: 'sky', icon: Laptop, testid: 'portal-remote-balance', carryover: 0, subtitle: 'días completos/mes' },
        ].map((item) => (
          <div key={item.label} data-testid={item.testid} className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className={`h-9 w-9 rounded-xl bg-${item.color}-100 dark:bg-${item.color}-900/30 flex items-center justify-center`}>
                <item.icon className={`h-5 w-5 text-${item.color}-600 dark:text-${item.color}-400`} />
              </div>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{item.label.split(' ')[0]}</span>
            </div>
            {loading ? (
              <div className="h-9 w-20 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse" />
            ) : (
              <>
                <p className={`text-3xl font-bold text-${item.color}-600 dark:text-${item.color}-400 font-heading`}>{item.value?.toFixed(1)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {item.subtitle ?? `de ${(item.total || 0) + (item.carryover || 0)} días totales`}
                  {!item.subtitle && item.carryover > 0 && <span className="text-xs text-emerald-600 ml-1">(+{item.carryover} arrastrados)</span>}
                </p>
                {!item.subtitle && (item.pending || 0) > 0 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />{item.pending?.toFixed(1)}d en revisión
                  </p>
                )}
                <div className="mt-3 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-${item.color}-500 rounded-full transition-all duration-700`}
                    style={{ width: `${Math.min(100, ((item.used || 0) / ((item.total || 1) + (item.carryover || 0))) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Contact HR — for balance discrepancies */}
      <button
        onClick={() => setShowContactHR(true)}
        data-testid="btn-contact-hr"
        className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-brand transition-colors -mt-2"
      >
        <Mail className="h-3 w-3" />
        ¿Algo no te cuadra con tu saldo? Envía una consulta a RRHH
      </button>
      <ContactHRDialog open={showContactHR} onClose={() => setShowContactHR(false)} />

      {/* Half-day quota info — only shown when user has used some half-days */}
      {remHalfUsed > 0 && remHalfAvail !== null && (
        <div className="flex items-center gap-2 px-1 -mt-2">
          <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-purple-400 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, (remHalfUsed / 8) * 100)}%` }} />
          </div>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {remHalfAvail} <span className="text-purple-500 font-medium">medias jornadas</span> disponibles (de 8/mes)
          </span>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">Acciones rápidas</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {quickActions.map((item) => (
            <motion.button
              key={item.testid}
              onClick={item.action}
              data-testid={item.testid}
              title={item.tooltip}
              whileHover={item.disabled ? {} : { scale: 1.02 }}
              whileTap={item.disabled ? {} : { scale: 0.97 }}
              className={`${item.color} rounded-xl p-4 flex items-center gap-3 font-medium text-sm transition-colors shadow-sm text-left`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="leading-tight">{item.label}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Weekly mini calendar */}
      <WeeklyMiniCalendar remotes={remotes || []} />

      {/* Recent requests */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Solicitudes recientes</h2>
          <button onClick={() => setActiveTab('requests')} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-medium">
            Ver todas →
          </button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />)}</div>
        ) : allRequests.length === 0 ? (
          <div className="bg-white dark:bg-slate-800/60 border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-8 text-center">
            <p className="text-sm text-slate-400 dark:text-slate-500">No tienes solicitudes todavía</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allRequests.map((req) => (
              <div key={req.id} data-testid={`portal-req-${req.id}`}
                className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${req.kind === 'vacation' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-sky-100 dark:bg-sky-900/30'}`}>
                    {req.kind === 'vacation'
                      ? <Palmtree className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      : <Laptop className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                    }
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {req.kind === 'vacation' ? 'Vacaciones' : 'Teletrabajo'}
                      <span className="font-normal text-slate-400 ml-1.5 text-xs">
                        {req.start_date && `${req.start_date}${req.end_date && req.end_date !== req.start_date ? ` → ${req.end_date}` : ''}`}
                        {req.date && req.date}
                      </span>
                    </p>
                    {req.working_days && <p className="text-xs text-slate-400">{req.working_days} días laborables</p>}
                  </div>
                </div>
                <StatusBadge status={req.status} />
              </div>
            ))}
          </div>
        )}
      </div>
      <DirectoryHighlights navigate={navigate} />
    </motion.div>
  );
}

// ─── Directory Highlights (new joiners + work anniversaries this month) ───
function DirectoryHighlights({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    directoryApi.highlights()
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  const { new_joiners = [], anniversaries = [] } = data;
  if (new_joiners.length === 0 && anniversaries.length === 0) return null;

  const monthName = new Date().toLocaleString('es-ES', { month: 'long' });
  const tabs = [];
  if (new_joiners.length) tabs.push({ key: 'new', label: 'Nuevas incorporaciones', count: new_joiners.length });
  if (anniversaries.length) tabs.push({ key: 'ann', label: 'Aniversarios', count: anniversaries.length });

  return (
    <div
      className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm"
      data-testid="home-directory-highlights"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center text-white">
            <Sparkles className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">En Valoriza este {monthName}</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Conoce a quienes se incorporan y celebra aniversarios
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/directory')}
          className="text-xs text-brand hover:underline flex items-center gap-0.5"
          data-testid="home-directory-link"
        >
          Ver directorio
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      <HighlightCarousel new_joiners={new_joiners} anniversaries={anniversaries} navigate={navigate} />
    </div>
  );
}

function HighlightCarousel({ new_joiners, anniversaries, navigate }) {
  const [tab, setTab] = useState(new_joiners.length ? 'new' : 'ann');
  const tabsCfg = [
    { key: 'new', label: `Nuevas (${new_joiners.length})`, items: new_joiners, color: 'emerald' },
    { key: 'ann', label: `Aniversarios (${anniversaries.length})`, items: anniversaries, color: 'amber' },
  ].filter(t => t.items.length > 0);
  if (tabsCfg.length === 1) {
    // No tabs needed, show directly
    return <PeopleStrip items={tabsCfg[0].items} kind={tabsCfg[0].key} navigate={navigate} />;
  }
  const current = tabsCfg.find(t => t.key === tab) || tabsCfg[0];
  return (
    <>
      <div className="flex gap-1.5 mb-3">
        {tabsCfg.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`highlight-tab-${t.key}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
              tab === t.key
                ? 'bg-brand text-white border-brand'
                : 'bg-card hover:bg-muted border-border text-muted-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <PeopleStrip items={current.items} kind={current.key} navigate={navigate} />
    </>
  );
}

function PeopleStrip({ items, kind, navigate }) {
  const fmtDay = (iso) => {
    try { return new Date(iso + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }); }
    catch { return iso; }
  };
  const visible = items.slice(0, 8);
  return (
    <div className="flex gap-3 overflow-x-auto -mx-1 px-1 pb-1" data-testid={`highlight-strip-${kind}`}>
      {visible.map((p) => (
        <button
          key={p.id}
          onClick={() => navigate('/directory')}
          className="flex-shrink-0 w-36 text-center group"
          data-testid={`highlight-person-${p.id}`}
        >
          <div
            className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center font-bold text-white shadow-md ring-2 ring-white/40 dark:ring-slate-800/40 mb-2 group-hover:scale-105 transition-transform"
            style={{ background: `linear-gradient(135deg, ${p.avatar_colour}, ${p.avatar_colour}aa)` }}
          >
            {p.initials}
          </div>
          <div className="text-xs font-semibold truncate group-hover:text-brand transition-colors">{p.full_name}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{p.puesto || '—'}</div>
          {kind === 'new' && p.fecha_inicio && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">
              Alta {fmtDay(p.fecha_inicio)}
            </div>
          )}
          {kind === 'ann' && p.anniversary_years && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
              {p.anniversary_years} {p.anniversary_years === 1 ? 'año' : 'años'} · día {p.anniversary_day}
            </div>
          )}
        </button>
      ))}
      {items.length > visible.length && (
        <button
          onClick={() => navigate('/directory')}
          className="flex-shrink-0 w-36 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center text-xs text-slate-500 hover:border-brand hover:text-brand transition-colors min-h-[110px]"
          data-testid="highlight-see-all"
        >
          <span className="text-base font-bold">+{items.length - visible.length}</span>
          <span>Ver todos</span>
        </button>
      )}
    </div>
  );
}
function ClockSection() {
  const [todayRecords, setTodayRecords] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [punchResult, setPunchResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(today);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, histRes] = await Promise.all([
        clockApi.today(),
        clockApi.myHistory({ date_from: dateFrom, date_to: dateTo }),
      ]);
      setTodayRecords(todayRes.data);
      setHistory(histRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const entries = todayRecords.filter(r => r.type === 'entry');
  const exits = todayRecords.filter(r => r.type === 'exit');
  const isIn = entries.length > exits.length;
  const totalHoursPortal = exits.reduce((sum, r) => sum + (r.hours_worked || 0), 0);
  const dayComplete = entries.length > 0 && exits.length > 0 && totalHoursPortal >= 8;
  const canPunch = !dayComplete;
  const lastEntry = entries[entries.length - 1];
  const lastExit = exits[exits.length - 1];

  const [liveHours, setLiveHours] = useState(null);
  useEffect(() => {
    if (!isIn || !lastEntry) { setLiveHours(null); return; }
    const tick = () => setLiveHours((Date.now() - new Date(lastEntry.timestamp)) / 3600000);
    tick();
    const t = setInterval(tick, 10000);
    return () => clearInterval(t);
  }, [isIn, lastEntry]);

  const weekTotal = history.reduce((s, d) => {
    const w = new Date(); w.setDate(w.getDate() - 7);
    return new Date(d.date + 'T12:00') >= w ? s + (d.total_hours || 0) : s;
  }, 0);
  const monthTotal = history.reduce((s, d) => s + (d.total_hours || 0), 0);

  const handlePunch = async () => {
    setPunching(true); setPunchResult(null);
    try {
      const res = await clockApi.punch({});
      setPunchResult({ success: true, ...res.data });
      await load();
    } catch (e) {
      setPunchResult({ success: false, error: e.response?.data?.detail || 'Error al fichar' });
    } finally { setPunching(false); }
  };

  return (
    <motion.div key="clock" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5">
      {/* Clock widget */}
      <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-8 shadow-sm text-center">
        <div className="text-4xl sm:text-5xl text-slate-800 dark:text-slate-100 mb-1">
          <LiveClock />
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
          dayComplete ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          : isIn ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400'
          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
        }`}>
          <span className={`h-2 w-2 rounded-full ${dayComplete ? 'bg-emerald-500' : isIn ? 'bg-sky-500 animate-pulse' : 'bg-slate-400'}`} />
          {dayComplete ? 'Jornada completada' : (entries.length > 0 && exits.length > 0) ? 'Jornada en curso' : isIn ? 'En teletrabajo' : 'Sin fichar hoy'}
        </div>

        {isIn && liveHours != null && (
          <div className="mb-5 flex items-center justify-center gap-2 text-sky-600 dark:text-sky-400">
            <Timer className="h-4 w-4 animate-pulse" />
            <span className="text-xl font-mono font-bold">{hoursLabel(liveHours)}</span>
            <span className="text-sm text-slate-500">trabajadas</span>
          </div>
        )}

        {canPunch && (
          <motion.button
            onClick={handlePunch}
            disabled={punching}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            data-testid="btn-punch"
            className={`mx-auto flex items-center justify-center gap-3 px-10 py-4 rounded-2xl text-white font-bold text-lg shadow-lg transition-all ${
              isIn
                ? 'bg-gradient-to-br from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600 shadow-red-200 dark:shadow-red-900/30'
                : 'bg-gradient-to-br from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 shadow-emerald-200 dark:shadow-emerald-900/30'
            }`}
          >
            {punching ? <Loader className="h-5 w-5 animate-spin" /> : isIn ? <LogOutIcon className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
            {punching ? 'Registrando...' : isIn ? 'Registrar Salida' : 'Registrar Entrada'}
          </motion.button>
        )}

        {dayComplete && <p className="mt-3 text-sm text-slate-400">Has completado tu jornada ({totalHoursPortal.toFixed(1)}h).</p>}
        {!dayComplete && entries.length > 0 && exits.length > 0 && (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            Llevas {totalHoursPortal.toFixed(1)}h registradas. Puedes continuar fichando.
          </p>
        )}

        <AnimatePresence>
          {punchResult && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4 space-y-2">
              {punchResult.success ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                    <CheckCircle className="h-4 w-4" />{punchResult.message}
                  </div>
                  {punchResult.warnings?.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-xs text-amber-700 dark:text-amber-300 text-left">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{w}
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />{punchResult.error}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Today timeline */}
      {todayRecords.length > 0 && (
        <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-emerald-500" />Fichajes de hoy
          </h3>
          <div className="space-y-2">
            {todayRecords.map((r) => (
              <div key={r.id} className="flex items-center gap-3" data-testid={`clock-record-${r.id}`}>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${r.type === 'entry' ? 'bg-sky-100 dark:bg-sky-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                  {r.type === 'entry' ? <LogIn className="h-4 w-4 text-sky-600 dark:text-sky-400" /> : <LogOutIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {r.type === 'entry' ? 'Entrada' : 'Salida'} — {fmtTime(r.timestamp)}
                  </div>
                  {r.hours_worked != null && <div className="text-xs text-slate-400">{hoursLabel(r.hours_worked)} trabajadas</div>}
                </div>
              </div>
            ))}
          </div>
          {lastExit?.hours_worked != null && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between text-sm">
              <span className="text-slate-400">Total de hoy</span>
              <span className="font-bold text-slate-700 dark:text-slate-200">{hoursLabel(lastExit.hours_worked)}</span>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Esta semana', value: hoursLabel(weekTotal), sub: `${history.filter(d => { const w = new Date(); w.setDate(w.getDate() - 7); return new Date(d.date + 'T12:00') >= w; }).length} días` },
          { label: 'Este mes', value: hoursLabel(monthTotal), sub: `${history.length} días` },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100 font-heading">{s.value}</div>
            <div className="text-xs text-slate-400 mt-0.5">{s.label}</div>
            <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* History */}
      <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          onClick={() => setShowHistory(h => !h)}
          data-testid="toggle-history"
        >
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Historial de fichajes</span>
          {showHistory ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </button>
        <AnimatePresence>
          {showHistory && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
              <div className="px-5 pb-4 pt-1 border-t border-slate-100 dark:border-slate-700">
                <div className="flex gap-2 mb-3 items-center flex-wrap">
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="text-xs border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200" />
                  <span className="text-xs text-slate-400">—</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="text-xs border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200" />
                  <button onClick={load} className="h-7 px-2 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400 transition-colors">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                </div>
                {loading ? (
                  <div className="py-6 text-center"><Loader className="h-5 w-5 animate-spin mx-auto text-emerald-500" /></div>
                ) : history.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">Sin registros en ese período</div>
                ) : (
                  <div className="space-y-2 pb-1" data-testid="history-list">
                    {history.map(day => (
                      <div key={day.date} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-700/40">
                        <div>
                          <div className="text-sm font-medium capitalize text-slate-700 dark:text-slate-200">{fmtDate(day.date)}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {day.records.filter(r => r.type === 'entry').map(r => fmtTime(r.timestamp)).join(', ')}
                            {day.records.some(r => r.type === 'exit') && ` → ${day.records.filter(r => r.type === 'exit').map(r => fmtTime(r.timestamp)).join(', ')}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold ${day.status === 'complete' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {day.total_hours ? hoursLabel(day.total_hours) : '--'}
                          </div>
                          <div className="text-xs text-slate-400">{day.status === 'complete' ? 'Completo' : day.status === 'in_progress' ? 'En curso' : 'Incompleto'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─── REQUESTS SECTION ────────────────────────────────────────────────────────
function RequestsSection({ vacations, remotes, onRefresh, loading }) {
  const [reqTab, setReqTab] = useState('vacations');
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const openCancel = (type, id, label) => { setCancelDialog({ type, id, label }); setCancelReason(''); setError(''); };

  const confirmCancel = async () => {
    if (!cancelDialog) return;
    const { type, id } = cancelDialog;
    setProcessing(p => ({ ...p, [id]: true }));
    setError('');
    try {
      if (type === 'vacation') await vacationApi.cancel(id, cancelReason);
      else await remoteApi.cancel(id, cancelReason);
      setCancelDialog(null);
      const msg = type === 'vacation'
        ? 'Solicitud de cancelación enviada a tu responsable.'
        : 'Teletrabajo cancelado. Tu responsable ha sido notificado.';
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 4000);
      await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al solicitar la cancelación');
    } finally { setProcessing(p => ({ ...p, [id]: false })); }
  };

  const canCancel = (status) => status === 'pending' || status === 'approved';

  // Can only cancel if the period has not started yet (strictly before today).
  // For past/started periods, employees must contact Admón. de Personal.
  const isCancelable = (req) => {
    if (!canCancel(req.status)) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(req.start_date + 'T00:00:00');
    return start > today;
  };

  return (
    <motion.div key="requests" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
      {successMsg && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 text-sm text-emerald-700 dark:text-emerald-300" data-testid="success-msg">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />{successMsg}
        </div>
      )}

      {[...(vacations || []), ...(remotes || [])].some(r => r.status === 'cancellation_requested') && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-sm text-orange-700 dark:text-orange-300" data-testid="cancellation-pending-notice">
          <Clock className="h-4 w-4 flex-shrink-0" />
          Tienes cancelaciones pendientes de aprobación por tu responsable.
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
        {[
          { id: 'vacations', label: `Vacaciones (${vacations?.length || 0})`, icon: Palmtree, testid: 'tab-vacations' },
          { id: 'remote', label: `Teletrabajo (${remotes?.length || 0})`, icon: Laptop, testid: 'tab-remote' },
        ].map(t => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setReqTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              reqTab === t.id
                ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm space-y-2">
        {loading ? (
          <div className="py-6 text-center"><Loader className="h-5 w-5 animate-spin text-emerald-500 mx-auto" /></div>
        ) : reqTab === 'vacations' ? (
          (vacations || []).length === 0 ? (
            <div className="py-8 text-center text-slate-400"><Palmtree className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No tienes solicitudes de vacaciones</p></div>
          ) : (vacations || []).map(req => (
            <div key={req.id} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-700/40 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors" data-testid={`vacation-row-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <Palmtree className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{req.start_date} → {req.end_date}</div>
                  <div className="text-xs text-slate-400">{req.working_days} días laborables</div>
                  {req.approver_comment && <div className="text-xs text-slate-400 italic truncate">"{req.approver_comment}"</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <StatusBadge status={req.status} />
                {canCancel(req.status) && (
                  isCancelable(req) ? (
                    <button
                      onClick={() => openCancel('vacation', req.id, `${req.start_date} - ${req.end_date}`)}
                      disabled={!!processing[req.id]}
                      data-testid={`cancel-vacation-${req.id}`}
                      className="h-7 px-2 rounded-lg text-xs text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      {processing[req.id] ? <Loader className="h-3.5 w-3.5 animate-spin" /> : 'Cancelar'}
                    </button>
                  ) : (
                    <span
                      title="No puedes cancelar un periodo ya iniciado o pasado. Contacta con Admón. de Personal."
                      data-testid={`cancel-vacation-disabled-${req.id}`}
                      className="h-7 px-2 rounded-lg text-xs text-slate-300 dark:text-slate-600 cursor-not-allowed inline-flex items-center"
                    >
                      Cancelar
                    </span>
                  )
                )}
              </div>
            </div>
          ))
        ) : (
          (remotes || []).length === 0 ? (
            <div className="py-8 text-center text-slate-400"><Laptop className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No tienes solicitudes de teletrabajo</p></div>
          ) : (remotes || []).map(req => (
            <div key={req.id} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-700/40 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors" data-testid={`remote-row-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0">
                  <Laptop className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{req.date}</div>
                  <div className="text-xs text-slate-400">{periodLabels[req.period_type] || req.period_type}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <StatusBadge status={req.status} />
                {canCancel(req.status) && (
                  <button
                    onClick={() => openCancel('remote', req.id, req.date)}
                    disabled={!!processing[req.id]}
                    data-testid={`cancel-remote-${req.id}`}
                    className="h-7 px-2 rounded-lg text-xs text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    {processing[req.id] ? <Loader className="h-3.5 w-3.5 animate-spin" /> : 'Cancelar'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => { setCancelDialog(null); setCancelReason(''); setError(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {cancelDialog?.type === 'remote' ? 'Cancelar teletrabajo' : 'Solicitar cancelación'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {cancelDialog && <p className="text-sm text-slate-500">Cancelar: <strong>{cancelDialog.label}</strong></p>}
            {cancelDialog?.type === 'remote' ? (
              <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                La cancelacion se aplica de forma inmediata. Tu responsable recibira una notificacion, pero no necesita aprobarla.
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-xs text-orange-700 dark:text-orange-300 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                La cancelacion requiere aprobacion de tu responsable.
              </div>
            )}
            <Label className="text-sm font-medium">Motivo (opcional)</Label>
            <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="Explica el motivo..." rows={3} className="resize-none" data-testid="cancel-reason-input" />
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Volver</Button>
            <Button onClick={confirmCancel} disabled={cancelDialog && !!processing[cancelDialog.id]}
              data-testid="confirm-cancel-btn"
              className={cancelDialog?.type === 'remote' ? 'bg-blue-600 hover:bg-blue-700 text-white gap-2' : 'bg-orange-500 hover:bg-orange-600 text-white gap-2'}>
              {cancelDialog && processing[cancelDialog.id]
                ? <><Loader className="h-3.5 w-3.5 animate-spin" />Cancelando...</>
                : cancelDialog?.type === 'remote' ? 'Cancelar teletrabajo' : 'Solicitar cancelacion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ─── PLAN SECTION ────────────────────────────────────────────────────────────
function PlanSection({ balance, canTelework }) {
  const year = new Date().getFullYear();
  const [selectedDays, setSelectedDays] = useState([]);
  const [notes, setNotes] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [loading, setLoading] = useState(false);
  const [existingPlan, setExistingPlan] = useState(null);
  const [error, setError] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [dateError, setDateError] = useState('');
  const [changeDialog, setChangeDialog] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [changingDay, setChangingDay] = useState(false);
  const { user } = useAuth();

  const loadPlan = useCallback(async () => {
    setLoadingPlan(true);
    try {
      const [holRes, planRes] = await Promise.all([
        calendarApi.holidays(year),
        remoteApi.getAnnualPlan(year),
      ]);
      setHolidays(holRes.data.map(h => h.date));
      const myPlan = planRes.data.find(p => p.user_id === user?.id || !p.user_name);
      if (myPlan) {
        setExistingPlan(myPlan);
        setSelectedDays(myPlan.planned_days || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingPlan(false); }
  }, [year, user?.id]);

  useEffect(() => { if (canTelework) loadPlan(); else setLoadingPlan(false); }, [canTelework, loadPlan]);

  const addDate = () => {
    setDateError('');
    if (!dateInput) return;
    if (selectedDays.includes(dateInput)) { setDateError('Ese día ya está en tu plan'); return; }
    if (isWeekendDay(dateInput)) { setDateError('No puedes seleccionar fin de semana'); return; }
    if (holidays.includes(dateInput)) { setDateError('Ese día es festivo'); return; }
    if (selectedDays.length >= MAX_PLAN_DAYS) { setDateError(`Máximo ${MAX_PLAN_DAYS} días por año`); return; }
    setSelectedDays(prev => [...prev, dateInput].sort());
    setDateInput('');
  };

  const removeDay = (d) => { if (existingPlan?.status === 'approved') return; setSelectedDays(prev => prev.filter(x => x !== d)); };

  const handleSubmit = async () => {
    if (selectedDays.length === 0) { setError('Selecciona al menos 1 día'); return; }
    setLoading(true); setError('');
    try {
      await remoteApi.createAnnualPlan({ year, planned_days: selectedDays, notes });
      await loadPlan();
    } catch (err) { setError(err.response?.data?.detail || 'Error al enviar el plan'); }
    finally { setLoading(false); }
  };

  const handleChangeDay = async () => {
    if (!newDate || !changeDialog) return;
    setChangingDay(true); setError('');
    try {
      await remoteApi.changeAnnualPlanDay(existingPlan.id, { old_date: changeDialog.oldDate, new_date: newDate, reason: changeReason });
      setChangeDialog(null); setNewDate(''); setChangeReason('');
      await loadPlan();
    } catch (err) { setError(err.response?.data?.detail || 'Error al cambiar el día'); }
    finally { setChangingDay(false); }
  };

  const statusColors = {
    pending: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300',
    approved: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300',
    rejected: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300',
  };
  const statusLabels = { pending: 'Pendiente de aprobación', approved: 'Aprobado', rejected: 'Rechazado' };

  if (!canTelework) return (
    <motion.div key="plan" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-8 text-center shadow-sm">
      <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
      <h2 className="text-xl font-bold font-heading mb-2">Teletrabajo no disponible</h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Tu puesto no tiene habilitada la modalidad flex office.</p>
      <Button onClick={() => window.open('https://trebol.refined.site/login', '_blank')} className="bg-amber-500 hover:bg-amber-600 text-white gap-2">
        <ExternalLink className="h-4 w-4" />Ir a Trébol
      </Button>
    </motion.div>
  );

  if (loadingPlan) return (
    <motion.div key="plan-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center h-40">
      <Loader className="h-6 w-6 animate-spin text-emerald-500" />
    </motion.div>
  );

  const remAvail = balance ? balance.remote_total - balance.remote_used - balance.remote_pending : MAX_PLAN_DAYS;

  return (
    <motion.div key="plan" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5">
      {/* Status banner */}
      {existingPlan && (
        <div className={`rounded-xl border p-4 ${statusColors[existingPlan.status] || ''}`} data-testid="existing-plan-status">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="font-medium text-sm">Plan {year}: {statusLabels[existingPlan.status]}</span>
            </div>
            <span className="text-xs opacity-70">{existingPlan.planned_days?.length} días</span>
          </div>
          {existingPlan.approver_comment && <p className="text-xs mt-2 opacity-80 italic">"{existingPlan.approver_comment}"</p>}
        </div>
      )}

      {/* Progress */}
      <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Días seleccionados</span>
          <span className="text-sm font-bold text-sky-600 dark:text-sky-400">{selectedDays.length}/{MAX_PLAN_DAYS}</span>
        </div>
        <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-400 to-indigo-500 rounded-full transition-all duration-500"
            style={{ width: `${(selectedDays.length / MAX_PLAN_DAYS) * 100}%` }} />
        </div>
        <div className="flex justify-between mt-1 text-xs text-slate-400">
          <span>{remAvail.toFixed(1)} días disponibles</span>
          <span>{MAX_PLAN_DAYS} total año</span>
        </div>
      </div>

      {/* Day picker */}
      {(!existingPlan || existingPlan.status === 'rejected') && (
        <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm space-y-4">
          <Label className="text-sm font-semibold text-slate-700 dark:text-slate-200">Añadir días al plan {year}</Label>
          <div className="flex gap-2">
            <Input type="date" value={dateInput} onChange={e => { setDateInput(e.target.value); setDateError(''); }}
              min={`${year}-01-01`} max={`${year}-12-31`}
              className="flex-1" data-testid="day-picker-input" disabled={selectedDays.length >= MAX_PLAN_DAYS} />
            <Button type="button" onClick={addDate} disabled={!dateInput || selectedDays.length >= MAX_PLAN_DAYS}
              data-testid="btn-add-day" className="bg-sky-500 hover:bg-sky-600 text-white gap-1.5">
              <Plus className="h-4 w-4" />Añadir
            </Button>
          </div>
          {dateError && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{dateError}</p>}
        </div>
      )}

      {/* Selected days list */}
      <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Días del plan</h3>
        <div className="space-y-2" data-testid="selected-days-list">
          <AnimatePresence>
            {selectedDays.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="py-6 text-center text-slate-400 border border-dashed border-slate-200 dark:border-slate-600 rounded-xl text-sm">
                No has seleccionado días todavía
              </motion.div>
            )}
            {selectedDays.map((d, i) => (
              <motion.div key={d} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                className="flex items-center justify-between p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800"
                data-testid={`plan-day-${d}`}>
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">{i + 1}</div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 capitalize">{fmtDate(d)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {existingPlan?.status === 'approved' && (
                    <button onClick={() => { setChangeDialog({ oldDate: d }); setNewDate(''); }}
                      data-testid={`btn-change-day-${d}`}
                      className="h-7 px-2 rounded-lg text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-800/30 transition-colors" title="Cambiar día">
                      <ArrowLeftRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {(!existingPlan || existingPlan.status === 'rejected') && (
                    <button onClick={() => removeDay(d)} data-testid={`btn-remove-day-${d}`}
                      className="h-7 px-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Notes + Submit */}
      {(!existingPlan || existingPlan.status === 'rejected') && (
        <div className="bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm space-y-3">
          <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Notas (opcional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Preferencias o justificación..." rows={2} className="resize-none" data-testid="plan-notes" />
          {error && <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 text-sm text-red-700 dark:text-red-300" data-testid="plan-error"><AlertCircle className="h-4 w-4 flex-shrink-0" />{error}</div>}
          <Button onClick={handleSubmit} disabled={loading || selectedDays.length === 0}
            data-testid="btn-submit-plan"
            className="w-full bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white font-semibold h-11">
            {loading ? <><Loader className="h-4 w-4 animate-spin mr-2" />Enviando...</> : <><Laptop className="h-4 w-4 mr-2" />Enviar plan para aprobación</>}
          </Button>
        </div>
      )}

      {existingPlan?.status === 'pending' && (
        <div className="p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-sm text-yellow-800 dark:text-yellow-300 flex items-center gap-2">
          <Info className="h-4 w-4 flex-shrink-0" />Tu plan está pendiente de aprobación por tu responsable.
        </div>
      )}

      {/* Policy note */}
      <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex items-start gap-2">
        <Shield className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-slate-400 space-y-0.5">
          <p>Máximo {MAX_PLAN_DAYS} días por año · Requiere aprobación del responsable</p>
          <p>Cambios de día permitidos hasta <strong>24h antes</strong> · No requieren nueva aprobación</p>
        </div>
      </div>

      {/* Change day dialog */}
      <Dialog open={!!changeDialog} onOpenChange={() => { setChangeDialog(null); setNewDate(''); setChangeReason(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-sky-500" />Cambiar día</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-sm">
              <span className="text-slate-400">Día actual: </span>
              <span className="font-semibold capitalize text-slate-700 dark:text-slate-200">{changeDialog && fmtDate(changeDialog.oldDate)}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Nuevo día *</Label>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]} max={`${year}-12-31`} data-testid="new-day-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Motivo (opcional)</Label>
              <Textarea value={changeReason} onChange={e => setChangeReason(e.target.value)}
                placeholder="Motivo del cambio..." rows={2} className="resize-none" data-testid="change-reason" />
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="h-3 w-3" />Tu responsable será notificado pero no necesita aprobar.</p>
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialog(null)}>Cancelar</Button>
            <Button onClick={handleChangeDay} disabled={!newDate || changingDay} data-testid="confirm-change-day"
              className="bg-sky-600 hover:bg-sky-700 text-white gap-2">
              {changingDay ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

// ─── TOUR STEPS ──────────────────────────────────────────────────────────────
const PORTAL_TOUR_STEPS = [
  {
    target: null,
    icon: Sparkles,
    title: '¡Bienvenido a Valoriza HR!',
    description: 'Este es tu portal personal. En menos de 1 minuto te mostramos todo lo que puedes hacer aquí.',
    placement: 'center',
  },
  {
    target: 'header',
    title: 'Tu información en tiempo real',
    description: 'La cabecera muestra tu nombre, rol y saldo disponible de vacaciones y teletrabajo. Siempre actualizado.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="portal-vacation-balance"]',
    title: 'Saldo de vacaciones',
    description: 'Ves los días disponibles, consumidos y pendientes de aprobación. La barra de progreso te indica tu nivel de uso.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="portal-request-vacation"]',
    title: 'Solicitar vacaciones',
    description: 'Con un clic abre el formulario. El sistema calcula los días laborables y notifica a tu responsable por email.',
    placement: 'right',
  },
  {
    target: '[data-testid="portal-goto-clock"]',
    title: 'Sistema de fichaje',
    description: 'Si trabajas en remoto, registra tu entrada y salida aquí. El reloj funciona en tiempo real y calcula tus horas.',
    placement: 'top',
  },
  {
    target: 'nav.sticky',
    title: 'Cuatro pestañas principales',
    description: 'Inicio · Fichar · Solicitudes · Plan Anual — toda tu gestión laboral en un solo lugar.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="portal-tab-clock"]',
    title: 'Pestaña Fichar',
    description: 'Reloj en vivo, botón de entrada/salida, historial de jornadas y estadísticas semanales y mensuales.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="portal-tab-requests"]',
    title: 'Pestaña Solicitudes',
    description: 'Aquí están todas tus solicitudes de vacaciones y teletrabajo. Puedes solicitar cancelaciones con aprobación.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="portal-tab-plan"]',
    title: 'Plan Anual de Teletrabajo',
    description: 'Si tienes modalidad flex office, planifica tus 6 días de teletrabajo con una sola solicitud de aprobación.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="theme-toggle"]',
    title: 'Modo oscuro / claro',
    description: 'Cambia el tema visual según tu preferencia. La configuración se guarda automáticamente.',
    placement: 'bottom',
  },
  {
    target: null,
    icon: CheckCircle,
    title: '¡Listo para empezar!',
    description: 'Ya conoces lo esencial. Puedes volver a ver este tour en cualquier momento pulsando el botón de ayuda (?) en la esquina inferior derecha.',
    placement: 'center',
  },
];

// ─── MAIN PORTAL ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'home', label: 'Inicio', icon: Home },
  { id: 'clock', label: 'Fichar', icon: Clock },
  { id: 'requests', label: 'Solicitudes', icon: ClipboardList },
  { id: 'plan', label: 'Plan Anual', icon: CalendarDays },
];

export default function EmployeePortal() {
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('home');
  const [balance, setBalance] = useState(null);
  const [monthlyRemote, setMonthlyRemote] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [remotes, setRemotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrebolDialog, setShowTrebolDialog] = useState(false);

  // Tab indicator animation
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const el = tabRefs.current[activeTab];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeTab]);

  const loadData = useCallback(async () => {
    try {
      const [balRes, vacRes, remRes, monthlyRes] = await Promise.all([
        userApi.getBalance('me'),
        vacationApi.list({}),
        remoteApi.list({}),
        remoteApi.monthlyBalance(),
      ]);
      setBalance(balRes.data);
      setVacations(vacRes.data);
      setRemotes(remRes.data);
      setMonthlyRemote(monthlyRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const canTelework = true;
  const vacAvail = balance ? (balance.vacation_total + (balance.carryover_days || 0)) - balance.vacation_used - balance.vacation_pending : 0;
  const remAvailRaw = monthlyRemote
    ? (monthlyRemote.full_days_available ?? monthlyRemote.available)
    : (balance ? balance.remote_total - balance.remote_used - balance.remote_pending : 0);
  const vacAvailSafe = Number.isFinite(Number(vacAvail)) ? Number(vacAvail) : 0;
  const remAvailSafe = Number.isFinite(Number(remAvailRaw)) ? Number(remAvailRaw) : 0;
  const initials = ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase() || '?';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* ── HEADER ── */}
      <header className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-cyan-600 dark:from-emerald-800 dark:via-emerald-700 dark:to-cyan-800">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-2xl bg-white/20 flex items-center justify-center text-white font-bold text-lg font-heading shadow-inner">
                {initials}
              </div>
              <div>
                <h1 className="text-white font-bold text-base sm:text-lg font-heading leading-tight">
                  Hola, {user?.first_name}
                </h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-white/70 text-xs">{user?.role === 'admin' ? 'Administrador' : user?.role === 'manager' ? 'Responsable' : 'Empleado'}</span>
                  {!loading && (
                    <>
                      <BalancePill icon={Palmtree} label="Vac" value={vacAvailSafe.toFixed(1)} />
                      {canTelework && <BalancePill icon={Laptop} label="Tel" value={remAvailSafe.toFixed(1)} />}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button onClick={toggleLang} data-testid="lang-toggle"
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white text-xs font-medium">
                <Globe className="h-4 w-4" />
              </button>
              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} data-testid="theme-toggle"
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button
                data-testid="btn-download-manual-portal"
                title="Descargar Manual de Usuario (PDF)"
                onClick={async () => {
                  try {
                    const res = await reportApi.downloadUserGuide();
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Manual_Usuario_Valoriza_HR.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('Error descargando manual:', e);
                  }
                }}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white">
                <Download className="h-4 w-4" />
              </button>
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <button onClick={() => navigate(user.role === 'admin' ? '/admin' : '/manager')}
                  className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white/80 hover:text-white text-xs font-medium">
                  Panel <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
              <button onClick={() => { logout(); navigate('/login'); }} data-testid="portal-logout"
                className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── TAB BAR ── */}
      <div className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative flex">
            {TABS.map(tab => (
              <button
                key={tab.id}
                ref={el => { tabRefs.current[tab.id] = el; }}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`portal-tab-${tab.id}`}
                className={`relative flex items-center gap-2 px-4 sm:px-5 py-3.5 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <tab.icon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:block">{tab.label}</span>
                <span className="sm:hidden text-xs">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
            {/* Sliding indicator */}
            <div
              className="absolute bottom-0 h-0.5 bg-emerald-500 dark:bg-emerald-400 rounded-full transition-all duration-300 ease-out"
              style={{ left: indicator.left, width: indicator.width }}
            />
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <main className="max-w-4xl mx-auto px-4 py-6 pb-12">
        <AnimatePresence mode="wait">
          {activeTab === 'home' && (
            <ErrorBoundary section="el resumen de inicio">
              <HomeSection
                user={user}
                balance={balance}
                monthlyRemote={monthlyRemote}
                loading={loading}
                vacations={vacations}
                remotes={remotes}
                navigate={navigate}
                canTelework={canTelework}
                setActiveTab={setActiveTab}
                showTrebolDialog={showTrebolDialog}
                setShowTrebolDialog={setShowTrebolDialog}
              />
            </ErrorBoundary>
          )}
          {activeTab === 'clock' && (
            <ErrorBoundary section="el fichaje">
              <ClockSection />
            </ErrorBoundary>
          )}
          {activeTab === 'requests' && (
            <ErrorBoundary section="tus solicitudes">
              <RequestsSection
                vacations={vacations}
                remotes={remotes}
                onRefresh={loadData}
                loading={loading}
              />
            </ErrorBoundary>
          )}
          {activeTab === 'plan' && (
            <ErrorBoundary section="la planificación">
              <PlanSection balance={balance} canTelework={canTelework} />
            </ErrorBoundary>
          )}
        </AnimatePresence>
      </main>

      {/* Tour guiado */}
      <TourGuide tourId={`portal_${user?.id || 'emp'}`} steps={PORTAL_TOUR_STEPS} enabled />

      {/* My Time reminder for telework days */}
      <MyTimeReminderDialog />
    </div>
  );
}
